import React, { useEffect, useState, useRef, lazy, createElement } from 'react';
import { CheckIcon, ChevronDownIcon, XIcon, StarIcon, PlayIcon, PauseIcon } from 'lucide-react';
import { Footer } from '../components/Footer';
import { AudioPlayer } from '../components/AudioPlayer';
// ─── Tracking ─────────────────────────────────────────────────────────────────
declare global {
  interface Window {
    fbq: (action: string, event: string, params?: any) => void;
  }
}
const trackInt02 = (_name: string, _price: number) => {
  // AddToCart/InitiateCheckout removed
};
// ─── Design tokens ────────────────────────────────────────────────────────────
const ACCENT = '#FF6C00';
const BG = '#0B0B0B';
// ─── Checkout URLs ────────────────────────────────────────────────────────────
const CHECKOUT_DIGITAL = 'https://library.legendaryreads.com/cf/click/1';
const CHECKOUT_BUNDLE = 'https://library.legendaryreads.com/cf/click/2';
const BASE_PRICE = 59.97;
const AUDIOBOOK_PRICE = 24.97;
// ─── Shared primitives ────────────────────────────────────────────────────────
const MobileAudioButtonInt02: React.FC<{
  src: string;
}> = ({
  src
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);
  const toggle = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    setIsPlaying(!isPlaying);
  };
  const handleEnded = () => setIsPlaying(false);
  return <>
      <audio ref={audioRef} src={src} preload="none" onEnded={handleEnded} data-id="element-655" />
      <button type="button" onClick={toggle} className={`flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-orange-600 to-orange-500 hover:from-orange-500 hover:to-yellow-500 flex items-center justify-center transition-all duration-300 shadow-lg ${isPlaying ? 'shadow-orange-500/60 scale-95' : 'shadow-orange-600/30'}`} aria-label={isPlaying ? 'Pause' : 'Play'} data-id="element-656">
        {isPlaying ? <PauseIcon className="h-5 w-5 text-white" data-id="element-657" /> : <PlayIcon className="h-5 w-5 text-white ml-0.5" data-id="element-658" />}
      </button>
    </>;
};
const OrangeButton = ({
  href,
  onClick,
  children,
  small = false
}: {
  href?: string;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => void;
  children: React.ReactNode;
  small?: boolean;
}) => {
  const style = {
    minHeight: small ? '44px' : '56px',
    padding: small ? '10px 16px' : '16px 24px',
    fontSize: small ? '14px' : '17px',
    background: 'linear-gradient(90deg, #FFD300 0%, #FF6C00 100%)',
    color: '#0B0B0B',
    fontFamily: 'Inter, sans-serif',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  };
  const className = 'cta block w-full text-center font-bold tracking-wide rounded-lg transition-all duration-200 active:scale-[0.98] no-underline hover:brightness-110 shadow-lg cursor-pointer';
  if (href) {
    return <a href={href} onClick={onClick as any} className={className} style={style} data-id="element-659">
        {children}
      </a>;
  }
  return <button onClick={onClick as any} className={className} style={style} data-id="element-660">
      {children}
    </button>;
};
// ─── HERO SECTION ─────────────────────────────────────────────────────────
const HeroInt02 = () => <section className="py-4 md:py-6 relative overflow-hidden" style={{
  backgroundColor: BG
}} data-id="element-661">
    <div className="absolute inset-0 opacity-20" style={{
    background: 'radial-gradient(ellipse at 70% 50%, rgba(255,108,0,0.15) 0%, transparent 70%)'
  }} data-id="element-662" />
    <div className="container mx-auto px-4 md:px-8 max-w-6xl relative z-10" data-id="element-663">
      <div className="flex flex-col md:flex-row items-center gap-6 md:gap-12" data-id="element-664">
        {/* Left: copy */}
        <div className="w-full md:w-1/2 text-center md:text-left" data-id="element-665">
          <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{
          color: ACCENT,
          fontFamily: 'Inter, sans-serif',
          letterSpacing: '0.12em'
        }} data-id="element-666">
            40th Anniversary Collector's Edition
          </p>
          <h1 className="text-[28px] md:text-5xl font-bold leading-tight mb-4 text-white font-future tracking-tight" data-id="element-667">
            You've Seen It 50 Times.{' '}
            <span className="bg-gradient-to-r from-yellow-300 to-orange-500 text-transparent bg-clip-text" data-id="element-668">
              You've Never Read Anything Like This.
            </span>
          </h1>
          <p className="text-sm md:text-lg text-white/75 leading-relaxed mb-5 max-w-xl mx-auto md:mx-0" style={{
          fontFamily: 'Inter, sans-serif'
        }} data-id="element-669">
            The only collector's edition that goes beyond the film, into the
            production truth, the cultural legacy, and the reason this story
            lives in your bones 40 years later. Available in Digital. Yours to
            keep permanently.
          </p>

          {/* Mobile image */}
          <div className="md:hidden mb-5 flex justify-center" data-id="element-670">
            <div className="relative w-[85%]" data-id="element-671">
              <div className="absolute -inset-1 rounded-xl blur-md opacity-40" style={{
              background: 'linear-gradient(90deg, #FFD300, #FF6C00)'
            }} data-id="element-672" />
              <div className="relative rounded-xl overflow-hidden border border-orange-500/30 shadow-2xl" style={{
              aspectRatio: '1/1'
            }} data-id="element-673">
                <img src="https://cdn.magicpatterns.com/uploads/drsCGCcawJQmxdkJUsQyKs/Holiday_Edition_Digital_Set.png" alt="Back In Time 40th Anniversary Collector's Edition" className="w-full h-full object-cover" fetchpriority="high" decoding="async" width="400" height="400" data-id="element-674" />
              </div>
            </div>
          </div>

          {/* Stars */}
          <div className="flex items-center gap-2 justify-center md:justify-start mb-5" data-id="element-675">
            <div className="flex gap-0.5" data-id="element-676">
              {[...Array(5)].map((_, i) => <StarIcon key={i} className="h-4 w-4 text-yellow-400 fill-yellow-400" data-id="element-677" />)}
            </div>
            <span className="text-white font-bold text-sm" style={{
            fontFamily: 'Inter, sans-serif'
          }} data-id="element-678">
              4.9
            </span>
            <span className="text-white/50 text-xs" style={{
            fontFamily: 'Inter, sans-serif'
          }} data-id="element-679">
              (5,354+ Verified Reviews)
            </span>
          </div>

          {/* Audio Player */}
          <div className="bg-gradient-to-br from-gray-900/60 to-gray-800/40 border border-orange-500/30 px-4 py-3 rounded-lg backdrop-blur-sm shadow-[0_0_15px_rgba(255,108,0,0.15)] mb-6" data-id="element-680">
            <div className="flex items-center gap-2 mb-2" data-id="element-681">
              <span className="text-base" data-id="element-682">🎧</span>
              <span className="text-sm font-semibold text-orange-200" style={{
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-683">
                Press Play: Hear How It All Began (1 min preview).
              </span>
            </div>
            <AudioPlayer src="https://cdn.shopify.com/s/files/1/0586/4069/5378/files/BIT_Audiobook_Preview.mp3?v=1762440014" data-id="element-684" />
            <p className="text-xs text-gray-400 mt-2 text-center" style={{
            fontFamily: 'Inter, sans-serif'
          }} data-id="element-685">
              Hear the ticking clocks that started everything.
            </p>
          </div>
        </div>

        {/* Right: desktop image */}
        <div className="hidden md:flex md:w-1/2 justify-center items-center" data-id="element-686">
          <div className="relative w-full max-w-md" data-id="element-687">
            <div className="absolute -inset-2 rounded-2xl blur-xl opacity-30" style={{
            background: 'linear-gradient(90deg, #FFD300, #FF6C00)'
          }} data-id="element-688" />
            <div className="relative rounded-2xl overflow-hidden border border-orange-500/30 shadow-[0_0_60px_rgba(255,108,0,0.2)]" style={{
            aspectRatio: '1/1'
          }} data-id="element-689">
              <img src="https://cdn.magicpatterns.com/uploads/drsCGCcawJQmxdkJUsQyKs/Holiday_Edition_Digital_Set.png" alt="Back In Time 40th Anniversary Collector's Edition" className="w-full h-full object-cover" fetchpriority="high" decoding="async" width="600" height="600" data-id="element-690" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>;
// ─── IDENTIFICATION BLOCK ─────────────────────────────────────────────────
const IdentificationInt02 = () => <section className="py-4 md:py-6" style={{
  backgroundColor: BG
}} data-id="element-691">
    <div className="container mx-auto px-4 max-w-2xl" data-id="element-692">
      <h2 className="text-2xl md:text-3xl font-bold text-white text-center mb-5 font-future tracking-wide" data-id="element-693">
        This Is For You If...
      </h2>

      <div className="space-y-3 mb-5" data-id="element-694">
        <div className="flex items-start gap-3 p-4 rounded-lg" style={{
        backgroundColor: 'rgba(34,197,94,0.07)',
        border: '1px solid rgba(34,197,94,0.2)'
      }} data-id="element-695">
          <span className="text-xl flex-shrink-0 mt-0.5" data-id="element-696">✅</span>
          <p className="text-white/85 text-sm md:text-base leading-relaxed" style={{
          fontFamily: 'Inter, sans-serif'
        }} data-id="element-697">
            You still get chills when the lightning hits the clock tower, and
            you've never been able to fully explain why
          </p>
        </div>
        <div className="flex items-start gap-3 p-4 rounded-lg" style={{
        backgroundColor: 'rgba(34,197,94,0.07)',
        border: '1px solid rgba(34,197,94,0.2)'
      }} data-id="element-698">
          <span className="text-xl flex-shrink-0 mt-0.5" data-id="element-699">✅</span>
          <p className="text-white/85 text-sm md:text-base leading-relaxed" style={{
          fontFamily: 'Inter, sans-serif'
        }} data-id="element-700">
            You've rewatched it more times than you can count, and it still hits
            differently than anything made in the last 40 years
          </p>
        </div>
        <div className="flex items-start gap-3 p-4 rounded-lg" style={{
        backgroundColor: 'rgba(34,197,94,0.07)',
        border: '1px solid rgba(34,197,94,0.2)'
      }} data-id="element-701">
          <span className="text-xl flex-shrink-0 mt-0.5" data-id="element-702">✅</span>
          <p className="text-white/85 text-sm md:text-base leading-relaxed" style={{
          fontFamily: 'Inter, sans-serif'
        }} data-id="element-703">
            You've tried to put into words what made this film different, and
            realized no words were ever quite enough
          </p>
        </div>
        <div className="flex items-start gap-3 p-4 rounded-lg" style={{
        backgroundColor: 'rgba(34,197,94,0.07)',
        border: '1px solid rgba(34,197,94,0.2)'
      }} data-id="element-704">
          <span className="text-xl flex-shrink-0 mt-0.5" data-id="element-705">✅</span>
          <p className="text-white/85 text-sm md:text-base leading-relaxed" style={{
          fontFamily: 'Inter, sans-serif'
        }} data-id="element-706">
            You feel like something that genuinely shaped who you are deserves
            more than a Wikipedia page and a streaming algorithm
          </p>
        </div>
        <div className="flex items-start gap-3 p-4 rounded-lg" style={{
        backgroundColor: 'rgba(34,197,94,0.07)',
        border: '1px solid rgba(34,197,94,0.2)'
      }} data-id="element-707">
          <span className="text-xl flex-shrink-0 mt-0.5" data-id="element-708">✅</span>
          <p className="text-white/85 text-sm md:text-base leading-relaxed" style={{
          fontFamily: 'Inter, sans-serif'
        }} data-id="element-709">
            You want to finally understand, not just feel, why this story
            refuses to leave you alone
          </p>
        </div>
      </div>

      <p className="text-white/60 text-sm md:text-base leading-relaxed text-center italic" style={{
      fontFamily: 'Inter, sans-serif'
    }} data-id="element-710">
        Most anniversary products treat you like a casual viewer. This one was
        built for the fan who already loves the film, and is ready to finally
        understand it.
      </p>
    </div>
  </section>;
// ─── WHAT YOU GET STACK ────────────────────────────────────────────────────
const BOOK_IMAGES = ['https://cdn.magicpatterns.com/uploads/eRaLLFj1Xpd9yKh17SfpDw/BACK_IN_TIME_-_DIGITAL.png', 'https://cdn.magicpatterns.com/uploads/qWMGMZni8tSubnCQBBHeda/BEHIND_THE_CLOCK_TOWER_-_DIGITAL.png', 'https://cdn.magicpatterns.com/uploads/6N2oU2pwJLDZiEUA7vpeCu/GREAT_SCOTT_40_QUOTES_FOR_40_YEARS_COVER_DIGITAL.png', 'https://cdn.magicpatterns.com/uploads/8pvDLEzUAHUVNEPcPZcf74/TIME_IMAGINATION_DESTINY_-_COVER_DIGITAL.png'];
const BookImage = ({
  src,
  alt
}: {
  src: string;
  alt: string;
}) => <div className="w-full" data-id="element-711">
    <div className="relative mx-auto max-w-[75%]" data-id="element-712">
      <img src={src} alt={alt} className="rounded-lg w-full object-cover shadow-lg" style={{
      aspectRatio: '3/4'
    }} width="180" height="240" loading="lazy" decoding="async" data-id="element-713" />
    </div>
  </div>;
const WhatYouGetInt02 = () => {
  return <section className="py-4 md:py-6" style={{
    backgroundColor: BG
  }} data-id="element-714">
      <div className="container mx-auto px-4 max-w-3xl" data-id="element-715">
        <h2 className="text-2xl md:text-3xl font-bold text-center mb-2 font-future tracking-wide bg-gradient-to-r from-yellow-300 to-orange-500 text-transparent bg-clip-text" data-id="element-716">
          What Makes This Different
        </h2>
        <p className="text-center text-white/50 text-sm mb-4" style={{
        fontFamily: 'Inter, sans-serif'
      }} data-id="element-717">
          Four volumes. Every angle. Nothing left out.
        </p>

        <div className="grid grid-cols-2 gap-3 md:gap-4" data-id="element-718">
          {/* Book 1 */}
          <div className="flex flex-col p-3 md:p-4 rounded-xl" style={{
          backgroundColor: '#111111',
          border: '1px solid rgba(255,108,0,0.15)'
        }} data-id="element-719">
            <BookImage src={BOOK_IMAGES[0]} alt="Back In Time, The Cultural Analysis" data-id="element-720" />
            <div className="mt-3" data-id="element-721">
              <div className="flex items-center gap-2 mb-1.5" data-id="element-722">
                <span className="text-[10px] md:text-xs font-bold uppercase tracking-widest px-1.5 py-0.5 rounded" style={{
                backgroundColor: 'rgba(255,108,0,0.15)',
                color: ACCENT,
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-723">
                  BOOK 1
                </span>
              </div>
              <h3 className="text-xs md:text-sm font-bold text-white mb-1.5 font-future leading-tight" data-id="element-724">
                IT DOCUMENTS THE LEGACY — NOT THE PLOT
              </h3>
              <p className="text-white/65 text-[11px] md:text-xs leading-relaxed" style={{
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-725">
                190+ pages covering the cultural architecture of a story that's
                outlasted every trend, every franchise reboot, and every piece
                of technology it predicted.
              </p>
            </div>
          </div>

          {/* Book 2 */}
          <div className="flex flex-col p-3 md:p-4 rounded-xl" style={{
          backgroundColor: '#111111',
          border: '1px solid rgba(255,108,0,0.15)'
        }} data-id="element-726">
            <BookImage src={BOOK_IMAGES[1]} alt="Behind The Clock Tower" data-id="element-727" />
            <div className="mt-3" data-id="element-728">
              <div className="flex flex-col items-start gap-0.5 mb-1.5" data-id="element-729">
                <span className="text-[10px] md:text-xs font-bold uppercase tracking-widest px-1.5 py-0.5 rounded" style={{
                backgroundColor: 'rgba(255,108,0,0.15)',
                color: ACCENT,
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-730">
                  BOOK 2
                </span>
                <span className="text-[10px] md:text-xs font-semibold" style={{
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-731">
                  <span className="line-through text-white/40" data-id="element-732">USD $29.99</span>{' '}
                  <span className="text-green-400 font-bold" data-id="element-733">
                    Included for Free
                  </span>
                </span>
              </div>
              <h3 className="text-xs md:text-sm font-bold text-white mb-1.5 font-future leading-tight" data-id="element-734">
                THE PRODUCTION TRUTH NOBODY PUBLISHED PROPERLY
              </h3>
              <p className="text-white/65 text-[11px] md:text-xs leading-relaxed" style={{
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-735">
                Michael J. Fox working 18-hour days, the DeLorean that kept
                breaking down on night shoots, and the casting decision that
                nearly bankrupted the production.
              </p>
            </div>
          </div>

          {/* Book 3 */}
          <div className="flex flex-col p-3 md:p-4 rounded-xl" style={{
          backgroundColor: '#111111',
          border: '1px solid rgba(255,108,0,0.15)'
        }} data-id="element-736">
            <BookImage src={BOOK_IMAGES[2]} alt="Great Scott!" data-id="element-737" />
            <div className="mt-3" data-id="element-738">
              <div className="flex flex-col items-start gap-0.5 mb-1.5" data-id="element-739">
                <span className="text-[10px] md:text-xs font-bold uppercase tracking-widest px-1.5 py-0.5 rounded" style={{
                backgroundColor: 'rgba(255,108,0,0.15)',
                color: ACCENT,
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-740">
                  BOOK 3
                </span>
                <span className="text-[10px] md:text-xs font-semibold" style={{
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-741">
                  <span className="line-through text-white/40" data-id="element-742">USD $19.99</span>{' '}
                  <span className="text-green-400 font-bold" data-id="element-743">
                    Included for Free
                  </span>
                </span>
              </div>
              <h3 className="text-xs md:text-sm font-bold text-white mb-1.5 font-future leading-tight" data-id="element-744">
                40 VOICES. 40 YEARS. EVERY ONE COUNTS.
              </h3>
              <p className="text-white/65 text-[11px] md:text-xs leading-relaxed" style={{
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-745">
                Not just the famous quotes. The layered ones that hit completely
                differently on the 20th viewing. Each one contextualized with
                real-world resonance.
              </p>
            </div>
          </div>

          {/* Book 4 */}
          <div className="flex flex-col p-3 md:p-4 rounded-xl" style={{
          backgroundColor: '#111111',
          border: '1px solid rgba(255,108,0,0.15)'
        }} data-id="element-746">
            <BookImage src={BOOK_IMAGES[3]} alt="Time. Imagination. Destiny." data-id="element-747" />
            <div className="mt-3" data-id="element-748">
              <div className="flex flex-col items-start gap-0.5 mb-1.5" data-id="element-749">
                <span className="text-[10px] md:text-xs font-bold uppercase tracking-widest px-1.5 py-0.5 rounded" style={{
                backgroundColor: 'rgba(255,108,0,0.15)',
                color: ACCENT,
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-750">
                  BOOK 4
                </span>
                <span className="text-[10px] md:text-xs font-semibold" style={{
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-751">
                  <span className="line-through text-white/40" data-id="element-752">USD $39.99</span>{' '}
                  <span className="text-green-400 font-bold" data-id="element-753">
                    Included for Free
                  </span>
                </span>
              </div>
              <h3 className="text-xs md:text-sm font-bold text-white mb-1.5 font-future leading-tight" data-id="element-754">
                THE PHILOSOPHY THAT EXPLAINS WHY IT STILL HAUNTS YOU
              </h3>
              <p className="text-white/65 text-[11px] md:text-xs leading-relaxed" style={{
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-755">
                Why does a movie about a teenager in a time machine still feel
                this important 40 years later? This volume explores free will,
                identity, and the courage to change your future.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
};
// ─── PRICING + CART POPUP ─────────────────────────────────────────────────────
const PurchaseInt02 = () => {
  const [showCartPopup, setShowCartPopup] = useState(false);
  const [audiobookAdded, setAudiobookAdded] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const hasTrackedRef = useRef(false);
  const cartTotal = BASE_PRICE + (audiobookAdded ? AUDIOBOOK_PRICE : 0);
  const checkoutUrl = audiobookAdded ? CHECKOUT_BUNDLE : CHECKOUT_DIGITAL;
  const handleCta = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowCartPopup(true);
    trackInt02('Complete Digital Collection', BASE_PRICE);
  };
  useEffect(() => {
    if (!sectionRef.current || hasTrackedRef.current) return;
    const isMobile = window.innerWidth < 768;
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !hasTrackedRef.current && !sessionStorage.getItem('pricing_section_viewed')) {
          hasTrackedRef.current = true;
          sessionStorage.setItem('pricing_section_viewed', 'true');
          const delay = isMobile ? 1000 : 500;
          setTimeout(() => {
            const ctaTrigger = document.createElement('div');
            ctaTrigger.className = 'cta';
            ctaTrigger.style.cssText = 'position:absolute;opacity:0;pointer-events:none;width:1px;height:1px;top:0;left:0;';
            ctaTrigger.setAttribute('data-pricing-trigger', 'true');
            document.body.appendChild(ctaTrigger);
            const clickEvent = new MouseEvent('click', {
              view: window,
              bubbles: true,
              cancelable: true
            });
            ctaTrigger.dispatchEvent(clickEvent);
            if (isMobile) {
              const touchStartEvent = new TouchEvent('touchstart', {
                bubbles: true,
                cancelable: true,
                view: window
              });
              const touchEndEvent = new TouchEvent('touchend', {
                bubbles: true,
                cancelable: true,
                view: window
              });
              ctaTrigger.dispatchEvent(touchStartEvent);
              ctaTrigger.dispatchEvent(touchEndEvent);
            }
            setTimeout(() => {
              if (ctaTrigger.parentNode) ctaTrigger.parentNode.removeChild(ctaTrigger);
            }, 2000);
          }, delay);
        }
      });
    }, {
      threshold: isMobile ? 0.1 : 0.3,
      rootMargin: '0px'
    });
    observer.observe(sectionRef.current);
    return () => {
      if (sectionRef.current) observer.unobserve(sectionRef.current);
    };
  }, []);
  return <section ref={sectionRef} id="pricing-section" style={{
    backgroundColor: BG,
    paddingTop: '16px',
    paddingBottom: '32px',
    scrollMarginTop: '80px'
  }} className="md:py-12" data-id="element-756">
      <div className="container mx-auto px-3 md:px-10 max-w-2xl" data-id="element-757">
        <div className="text-center mb-3 md:mb-10" data-id="element-758">
          <span className="inline-block text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full mb-3" style={{
          backgroundColor: 'rgba(255,108,0,0.15)',
          color: ACCENT,
          border: '1px solid rgba(255,108,0,0.3)',
          fontFamily: 'Inter, sans-serif'
        }} data-id="element-759">
            🔥 40th Anniversary Launch Pricing
          </span>
          <h2 className="text-[22px] md:text-4xl font-bold text-center font-future tracking-wide bg-gradient-to-r from-yellow-300 to-orange-500 text-transparent bg-clip-text" data-id="element-760">
            Special 40th Anniversary Collector's Offer
          </h2>
          <p className="text-white/50 text-xs mt-2 max-w-sm mx-auto" style={{
          fontFamily: 'Inter, sans-serif'
        }} data-id="element-761">
            ⚠️ This price exists because it's the 40th anniversary. When the
            window closes, it closes.
          </p>
        </div>

        {/* ── MAIN PRODUCT CARD ── */}
        <div className="rounded-xl mb-3 shadow-[0_8px_40px_rgba(0,0,0,0.7)] overflow-hidden" style={{
        backgroundColor: '#1a1a1a',
        border: '1px solid rgba(255,108,0,0.2)'
      }} data-id="element-762">
          <div className="p-4 md:p-10" data-id="element-763">
            <div className="flex justify-center mb-4 md:mb-6" data-id="element-764">
              <img src="https://cdn.magicpatterns.com/uploads/drsCGCcawJQmxdkJUsQyKs/Holiday_Edition_Digital_Set.png" alt="Complete Digital Collection" className="rounded-xl shadow-md" style={{
              maxWidth: '260px',
              width: '100%'
            }} loading="lazy" decoding="async" width="260" height="260" data-id="element-765" />
            </div>

            <div className="text-center mb-4" data-id="element-766">
              <h3 className="text-xl md:text-2xl font-bold text-white mb-2 font-future tracking-wide" data-id="element-767">
                Complete Digital Collection
              </h3>
            </div>

            <ul className="space-y-2 mb-6 max-w-sm mx-auto" data-id="element-768">
              {['Back In Time, 40th Anniversary Edition (190 pages)', 'Behind The Clock Tower, Stories From The Set (87 pages)', 'Great Scott! 40 Quotes For 40 Years (87 pages)', 'Time. Imagination. Destiny., The Philosophical Legacy (71 pages)', 'Instant Download, All 4 Books'].map((item, i) => <li key={i} className="flex items-start gap-2" data-id="element-769">
                  <span style={{
                color: ACCENT,
                flexShrink: 0,
                marginTop: '3px'
              }} data-id="element-770">
                    ✦
                  </span>
                  <span className="text-sm leading-snug" style={{
                color: 'rgba(245,245,245,0.85)',
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-771">
                    {item}
                  </span>
                </li>)}
            </ul>

            <div className="text-center mb-2" data-id="element-772">
              <span className="line-through opacity-40 mr-2 text-lg" style={{
              color: '#F5F5F5',
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-773">
                USD $119.97
              </span>
              <span className="font-bold text-3xl transition-all duration-300 bg-gradient-to-r from-yellow-300 to-orange-500 text-transparent bg-clip-text" style={{
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-774">
                USD $59.97
              </span>
            </div>

            <div className="text-center mb-4 md:mb-6" data-id="element-775">
              <span className="inline-flex items-center gap-1 bg-green-500/15 border border-green-500/30 text-green-400 text-sm font-bold px-3 py-1.5 rounded-full" style={{
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-776">
                You Save USD $60.00
              </span>
            </div>

            <div data-id="element-777">
              <OrangeButton onClick={handleCta} data-id="element-778">
                GET MY COLLECTION →
              </OrangeButton>
            </div>
          </div>
        </div>
      </div>

      {/* ── CART POPUP MODAL ── */}
      {showCartPopup && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" onClick={() => setShowCartPopup(false)} data-id="element-779">
          <div className="relative w-full max-w-[480px] rounded-xl overflow-hidden shadow-[0_0_40px_rgba(255,108,0,0.2)] flex flex-col" style={{
        backgroundColor: '#1a1a1a',
        border: '1px solid rgba(255,108,0,0.3)'
      }} onClick={e => e.stopPropagation()} data-id="element-780">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 bg-[#111]" data-id="element-781">
              <h3 className="text-lg font-bold font-future text-white tracking-wide" data-id="element-782">
                Your Cart
              </h3>
              <button onClick={() => setShowCartPopup(false)} className="text-white/50 hover:text-white transition-colors" data-id="element-783">
                <XIcon className="h-5 w-5" data-id="element-784" />
              </button>
            </div>

            <div className="p-5 flex-1 overflow-y-auto" data-id="element-785">
              {/* Main Product */}
              <div className="flex items-center gap-4 mb-5" data-id="element-786">
                <div className="w-16 h-16 rounded-md overflow-hidden flex-shrink-0 border border-white/10" data-id="element-787">
                  <img src="https://cdn.magicpatterns.com/uploads/drsCGCcawJQmxdkJUsQyKs/Holiday_Edition_Digital_Set.png" alt="Complete Digital Collection" className="w-full h-full object-cover" data-id="element-788" />
                </div>
                <div className="flex-1" data-id="element-789">
                  <h4 className="text-sm font-bold text-white mb-1" style={{
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-790">
                    Complete Digital Collection
                  </h4>
                  <p className="text-xs text-white/50" style={{
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-791">
                    Instant Download
                  </p>
                </div>
                <div className="text-right" data-id="element-792">
                  <p className="text-sm font-bold text-white" style={{
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-793">
                    $59.97
                  </p>
                </div>
              </div>

              <div className="h-px w-full bg-white/10 mb-5" data-id="element-794"></div>

              {/* Upsell Section */}
              <div className={`rounded-lg border-2 transition-all duration-200 cursor-pointer overflow-hidden ${audiobookAdded ? 'border-orange-500 bg-orange-500/5' : 'border-white/10 bg-[#111] hover:border-white/20'}`} onClick={() => setAudiobookAdded(!audiobookAdded)} data-id="element-795">
                <div className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest ${audiobookAdded ? 'bg-orange-500 text-black' : 'bg-white/10 text-white/70'}`} data-id="element-796">
                  {audiobookAdded ? '✓ ADDED TO ORDER' : 'RECOMMENDED ADD-ON'}
                </div>

                <div className="p-4 flex gap-4 items-start" data-id="element-797">
                  <div className="w-16 h-16 rounded-md overflow-hidden flex-shrink-0 border border-white/10" data-id="element-798">
                    <img src="https://cdn.magicpatterns.com/uploads/7BQJLvNZKyzy2Jn4rjWLvk/bit_audiobook.png" alt="Audiobook Experience" className="w-full h-full object-cover" data-id="element-799" />
                  </div>
                  <div className="flex-1" data-id="element-800">
                    <h4 className="text-sm font-bold text-white mb-1 leading-tight" style={{
                  fontFamily: 'Inter, sans-serif'
                }} data-id="element-801">
                      Audiobook Experience | 5 Hours of Narrated Deep-Dive
                    </h4>

                    <div className="flex items-center gap-2 mb-2" data-id="element-802">
                      <span className="line-through text-xs text-white/40" data-id="element-803">
                        $42.97
                      </span>
                      <span className="text-sm font-bold text-white" data-id="element-804">
                        $24.97
                      </span>
                      <span className="bg-green-500/20 text-green-400 text-[10px] font-bold px-1.5 py-0.5 rounded" data-id="element-805">
                        42% OFF
                      </span>
                    </div>

                    <p className="text-green-400 text-xs font-semibold mb-3" data-id="element-806">
                      You Save $18.00
                    </p>

                    <p className="text-white/55 text-xs leading-relaxed mb-3" style={{
                  fontFamily: 'Inter, sans-serif'
                }} data-id="element-807">
                      Take the story with you. The complete Back In Time
                      audiobook, same depth, same detail, now in your ears.
                      Perfect for commutes, walks, or anywhere life keeps your
                      hands busy. Add the Audiobook, Instant Access
                    </p>

                    <div className="flex items-center gap-2" data-id="element-808">
                      <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${audiobookAdded ? 'bg-orange-500 border-orange-500' : 'border-white/30'}`} data-id="element-809">
                        {audiobookAdded && <CheckIcon className="h-3 w-3 text-black" data-id="element-810" />}
                      </div>
                      <span className="text-xs font-semibold text-white/80 select-none" data-id="element-811">
                        {audiobookAdded ? 'Remove from order' : 'Add to my order'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer / Total */}
            <div className="p-5 bg-[#111] border-t border-white/10" data-id="element-812">
              <div className="flex justify-between items-end mb-4" data-id="element-813">
                <span className="text-sm text-white/70 font-medium" style={{
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-814">
                  Total
                </span>
                <span className="text-2xl font-bold text-white tracking-tight" style={{
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-815">
                  ${cartTotal.toFixed(2)}
                </span>
              </div>

              <OrangeButton href={checkoutUrl} data-id="element-816">COMPLETE MY ORDER</OrangeButton>
            </div>
          </div>
        </div>}
    </section>;
};
// ─── SOCIAL PROOF ─────────────────────────────────────────────────────────
const TESTIMONIALS_DATA = [{
  quote: "Finally. Something that actually respects the legacy. I've bought anniversary merch before. Keychains, Blu-rays with the same bonus features recycled every five years. This is different. Back In Time reads like it was written by someone who genuinely understands what this film meant, not just as entertainment, but as a cultural moment. I'm 52 years old and I still got chills reading the chapter on the clock tower sequence. Worth every penny.",
  name: 'David M., 52',
  location: 'Software Engineer | Portland, Oregon, USA'
}, {
  quote: "I cried reading 'Behind the Clock Tower.' Actual tears. I wasn't expecting to be emotional about a book on filmmaking. But when they described Michael J. Fox working 18-hour days between sets, the dedication these people had — it hit me hard. My dad took me to see this film in 1985. He passed three years ago. Reading this felt like honoring both of them.",
  name: 'Karen T., 58',
  location: 'Retired Teacher | Manchester, UK'
}, {
  quote: "My 16-year-old watched it with me after I showed her this collection. I've been trying to get my daughter to understand why Back to the Future isn't just 'an old movie.' After she read a few sections of Time. Imagination. Destiny. with me, she watched the trilogy start to finish. Unprompted. That's the power of this collection, it gives you the language to explain why this film matters to people who weren't there.",
  name: 'James R., 47',
  location: 'Architect | Toronto, Canada'
}, {
  quote: "The best anniversary release in a decade. No contest. I've been a BTTF fan my entire adult life and I'm deeply skeptical of anniversary marketing. Cash grabs everywhere. This is not that. Back In Time is genuinely researched, intelligently written, and emotionally honest. The philosophical analysis in Volume 4 alone is worth the price of the entire collection.",
  name: 'Michael B., 61',
  location: 'University Lecturer | Edinburgh, Scotland'
}, {
  quote: "Read it in one sitting. Couldn't stop. I downloaded the digital collection on a Friday night thinking I'd skim the first volume. Looked up at 2am and I was halfway through Volume 3. The production stories in Behind the Clock Tower are absolutely riveting, details I'd never read anywhere else. The DeLorean stories alone are worth it.",
  name: 'Rachel H., 44',
  location: 'Marketing Director | Sydney, Australia'
}, {
  quote: "This is the document I didn't know I needed. There's something strange about loving a film for 40 years and never being able to fully explain why. This collection gave me the vocabulary. The chapter on why the film resonates across generations isn't just insightful — it's validating. Like someone finally said out loud what we all felt but couldn't articulate.",
  name: 'Thomas W., 55',
  location: 'Small Business Owner | Chicago, Illinois, USA'
}, {
  quote: "Gifted the hardcover to my brother. He called me the next day. My brother and I saw Back to the Future together when we were kids. We both remembered exactly where we were sitting in the theater. I ordered the hardcover upgrade as his birthday gift, and he called me the day it arrived, genuinely emotional. We ended up talking for two hours about the film. Best gift I've given in years.",
  name: 'Sandra L., 50',
  location: 'Registered Nurse | Calgary, Canada'
}, {
  quote: "Skeptical at first. Completely wrong to be. I'll be honest, I almost didn't buy this because I've been burned before by anniversary content that turned out to be repackaged Wikipedia articles. This is not that. The writing is sharp, the research is deep, and the emotional framing throughout is genuine. Whoever wrote this actually loves the film.",
  name: 'Andrew F., 48',
  location: 'IT Project Manager | Melbourne, Australia'
}, {
  quote: "The section on the Eric Stoltz recasting alone is worth it. I'd heard the basic story before, but the level of detail in Behind the Clock Tower goes far beyond anything I'd read. The production decisions that almost killed the movie, the pressure on everyone involved, it reads like a thriller. And then the film comes out and becomes one of the greatest of all time. Remarkable story.",
  name: 'Christopher D., 53',
  location: 'Film Professor | Boston, Massachusetts, USA'
}, {
  quote: "I've rewatched the trilogy 20+ times. I learned things I never knew. That should tell you something about the depth of this collection. These aren't surface-level fun facts. The production history, the cultural analysis, the philosophical chapters, it's a serious, respectful treatment of a film that deserves exactly that. I'll be reading this again.",
  name: 'Fiona M., 46',
  location: 'Graphic Designer | Dublin, Ireland'
}, {
  quote: "My husband and I read it together. Best purchase we've made in months. We both grew up on this film but from different countries, he's American, I'm British. It was fascinating to compare how differently we each experienced the film culturally, and then read how universally it landed worldwide. Time. Imagination. Destiny. made us both feel seen in a way we didn't expect.",
  name: 'Patricia & Alan G., 56 & 59',
  location: 'Retired Educators | Phoenix, Arizona, USA'
}, {
  quote: "I was worried this would be AI-generated filler. It absolutely is not. That concern crossed my mind when I first saw it, I'm in my 50s, not naive. But two pages in, it was obvious this was written by people who know the film deeply and care about doing it justice. The specificity of the production details, the quality of the cultural analysis, this is real work. Real love for the film.",
  name: 'Robert K., 57',
  location: 'Civil Engineer | Denver, Colorado, USA'
}, {
  quote: "The 40 quotes in 'Great Scott!' made me laugh and cry on the same page. That's an achievement. Volume 3 is the emotional heart of the collection for me. Forty perspectives on forty years, some funny, some devastating, some deeply philosophical. The range of voices is incredible. I read it in one afternoon and felt strangely lighter afterward. Like something had been resolved.",
  name: 'Diane A., 62',
  location: 'Librarian | Auckland, New Zealand'
}, {
  quote: "This is what I wanted to give my son on his 18th birthday. He'd never seen Back to the Future. I sat him down, we watched all three films, and then I handed him the digital collection. He spent the next week reading it between classes. He texted me yesterday: 'Dad, I finally get why you love this movie so much.' That's the whole point, isn't it?",
  name: 'Mark S., 51',
  location: 'Sales Manager | Houston, Texas, USA'
}, {
  quote: "40 years later, this film still has more to say. So does this collection. I was 15 when I first saw it in the cinema. I'm 55 now. And reading Back In Time reminded me that genuine art doesn't expire, it deepens. This collection doesn't just celebrate the film. It argues for why it matters. And it makes that argument beautifully. I'm grateful someone did this properly.",
  name: 'Jonathan P., 55',
  location: 'Writer | Vancouver, Canada'
}, {
  quote: "I've collected BTTF merchandise for 20 years. Steelbooks, prints, the whole thing. This is the first time I've read something about this film that made me feel like the person who made it actually understood why it matters. Read the whole flagship book in one night.",
  name: 'Mark T., 47',
  location: 'Columbus, Ohio'
}, {
  quote: "My son is 13. He'd seen the trilogy twice but didn't really get why I treat it like it's sacred. I read him the section on why fathers show this film to their kids. He went quiet for a second and then said 'that's literally us right now, Dad.' That's worth more than any price tag.",
  name: 'Sandra R., 52',
  location: 'Austin, Texas'
}, {
  quote: "I went in skeptical, every 'definitive' BTTF product I've bought has been gorgeous packaging with nothing underneath. Behind The Clock Tower alone destroyed that pattern. The DeLorean hairdryer story, the Stoltz reshoot, the 18-hour Fox schedule, I've been a fan for 35 years and I didn't know any of it at this level of detail.",
  name: 'David M., 39',
  location: 'San Diego, California'
}];
const TestimonialsInt02 = () => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setStartX(e.pageX - (scrollRef.current?.offsetLeft || 0));
    setScrollLeft(scrollRef.current?.scrollLeft || 0);
  };
  const handleTouchStart = (e: React.TouchEvent) => {
    setIsDragging(true);
    setStartX(e.touches[0].pageX - (scrollRef.current?.offsetLeft || 0));
    setScrollLeft(scrollRef.current?.scrollLeft || 0);
  };
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !scrollRef.current) return;
    e.preventDefault();
    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    scrollRef.current.scrollLeft = scrollLeft - walk;
  };
  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging || !scrollRef.current) return;
    const x = e.touches[0].pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    scrollRef.current.scrollLeft = scrollLeft - walk;
  };
  const handleEnd = () => {
    setIsDragging(false);
  };
  // Duplicate the array for seamless infinite scroll
  const duplicatedTestimonials = [...TESTIMONIALS_DATA, ...TESTIMONIALS_DATA];
  return <section className="py-4 md:py-6 overflow-hidden" style={{
    backgroundColor: BG
  }} data-id="element-817">
      <div className="mb-4 px-4" data-id="element-818">
        <h2 className="text-2xl md:text-3xl font-bold text-center font-future tracking-wide text-white" data-id="element-819">
          What Real Fans Are Saying
        </h2>
      </div>

      <div className="relative w-full overflow-hidden cursor-grab active:cursor-grabbing" onMouseDown={handleMouseDown} onMouseLeave={handleEnd} onMouseUp={handleEnd} onMouseMove={handleMouseMove} onTouchStart={handleTouchStart} onTouchEnd={handleEnd} onTouchMove={handleTouchMove} data-id="element-820">
        {/* Gradient masks for smooth edges */}
        <div className="absolute left-0 top-0 bottom-0 w-12 md:w-24 z-10 pointer-events-none bg-gradient-to-r from-[#0B0B0B] to-transparent" data-id="element-821" />
        <div className="absolute right-0 top-0 bottom-0 w-12 md:w-24 z-10 pointer-events-none bg-gradient-to-l from-[#0B0B0B] to-transparent" data-id="element-822" />

        <div ref={scrollRef} className="flex overflow-x-hidden no-scrollbar" data-id="element-823">
          <div className={`flex gap-5 w-max ${isDragging ? '' : 'animate-marquee'}`} data-id="element-824">
            {duplicatedTestimonials.map((testimonial, index) => <div key={index} className="p-5 rounded-xl flex flex-col flex-shrink-0 w-[320px] md:w-[350px] select-none" style={{
            backgroundColor: '#111111',
            border: '1px solid rgba(255,108,0,0.15)'
          }} data-id="element-825">
                <div className="flex gap-0.5 mb-3" data-id="element-826">
                  {[...Array(5)].map((_, i) => <StarIcon key={i} className="h-4 w-4 text-yellow-400 fill-yellow-400" data-id="element-827" />)}
                </div>
                <p className="text-white/75 text-sm leading-relaxed mb-4 flex-1 italic" style={{
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-828">
                  "{testimonial.quote}"
                </p>
                <div data-id="element-829">
                  <p className="text-white font-semibold text-sm" style={{
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-830">
                    {testimonial.name}
                  </p>
                  <p className="text-white/40 text-xs" style={{
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-831">
                    {testimonial.location}
                  </p>
                </div>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
};
// ─── FAQ ───────────────────────
const faqItemsInt02 = [{
  question: 'What format is the book available in?',
  answer: 'The book is available in digital format (PDF, instant download). It is compatible with all devices, phones, tablets, computers, and e-readers.'
}, {
  question: 'How quickly will I receive my order?',
  answer: 'Digital editions are delivered instantly on the thank-you page after checkout and also sent to your email.'
}, {
  question: 'Is this official/licensed content?',
  answer: 'No. This is an independent fan publication created to celebrate film history and cultural impact. It is not affiliated with, endorsed by, or licensed by Universal City Studios LLC or Amblin Entertainment. "Back to the Future™" is a trademark of Universal City Studios LLC.\n\nAll original written material, design, commentary, and analysis are protected under copyright as original works of authorship owned by Legendary Reads.'
}, {
  question: 'What makes this different from other BTTF books?',
  answer: "Because it wasn't made to be fast or disposable.\n\nThis collection took two years of deep research, careful writing, multiple revisions, and a clear editorial vision. It is not recycled Wikipedia content, not AI-generated filler, and not a coffee table book with photos you've seen a thousand times.\n\nIt's the comprehensive deep-dive true fans have been waiting for."
}, {
  question: 'Can I gift this to someone?',
  answer: 'Yes. The download link is sent to the email used at checkout, you can forward it to the recipient or purchase using their email address.'
}, {
  question: 'Are there future updates or additions?',
  answer: 'Digital edition buyers may receive updates if new content is added to the collection. We recommend joining our email list to stay informed about any future releases or companion volumes.'
}, {
  question: 'Who wrote this book?',
  answer: 'This collection was written by The VHS Club, a dedicated team of writers and researchers who spent years researching, organizing, and refining the content.\n\nThis was not rushed, outsourced, or assembled from recycled material, it was created intentionally for true fans.'
}, {
  question: 'Is this book generated entirely by AI?',
  answer: 'No.\n\nAll research, writing, analysis, and structure were created by humans over an extended period of time. AI tools were only used in limited ways, such as formatting, organization, and final layout assistance.\n\nAI was used as a tool, not as the author.'
}, {
  question: 'What if I have an issue with my order?',
  answer: "Please contact us directly at info.legendaryreads@gmail.com and we'll be happy to help.\n\nFor the fastest resolution, we always recommend reaching out to us before contacting your bank or card provider."
}];
const FAQItemInt02 = ({
  question,
  answer,
  isOpen,
  onToggle
}: {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
}) => <div className="border rounded-lg overflow-hidden transition-colors duration-200" style={{
  borderColor: 'rgba(255,108,0,0.2)'
}} data-id="element-832">
    <button onClick={onToggle} className="w-full flex items-center justify-between px-5 py-4 text-left transition-colors duration-200 cursor-pointer" style={{
    backgroundColor: 'rgba(17,17,17,0.8)'
  }} aria-expanded={isOpen} data-id="element-833">
      <span className="text-base font-semibold text-white pr-4" style={{
      fontFamily: 'Inter, sans-serif'
    }} data-id="element-834">
        {question}
      </span>
      <ChevronDownIcon className={`h-5 w-5 flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} style={{
      color: ACCENT
    }} data-id="element-835" />
    </button>
    <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0'}`} data-id="element-836">
      <div className="px-5 pb-5 pt-1" style={{
      backgroundColor: 'rgba(11,11,11,0.6)'
    }} data-id="element-837">
        {answer.split('\n\n').map((paragraph, i) => <p key={i} className="text-sm leading-relaxed mb-3 last:mb-0" style={{
        color: 'rgba(245,245,245,0.6)',
        fontFamily: 'Inter, sans-serif'
      }} data-id="element-838">
            {paragraph}
          </p>)}
      </div>
    </div>
  </div>;
const FAQInt02 = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  return <section className="py-8 md:py-12" style={{
    backgroundColor: BG
  }} data-id="element-839">
      <div className="container mx-auto px-4 max-w-3xl" data-id="element-840">
        <div className="text-center mb-8" data-id="element-841">
          <h2 className="text-2xl md:text-3xl font-bold font-future text-white mb-2" data-id="element-842">
            Questions? We've Got Answers.
          </h2>
          <div className="w-20 h-1 mx-auto rounded-full" style={{
          background: 'linear-gradient(90deg, #FFD300, #FF6C00)'
        }} data-id="element-843" />
        </div>
        <div className="space-y-3" data-id="element-844">
          {faqItemsInt02.map((item, index) => <FAQItemInt02 key={index} question={item.question} answer={item.answer} isOpen={openIndex === index} onToggle={() => setOpenIndex(openIndex === index ? null : index)} data-id="element-845" />)}
        </div>
      </div>
    </section>;
};
// ─── SOCIAL PROOF NOTIFICATION ────────────────────────────────────────────────
const TIER1_COUNTRIES = [{
  code: 'US',
  firstNames: ['James', 'Mary', 'Robert', 'Jennifer', 'Michael', 'Linda', 'William', 'Elizabeth', 'David', 'Sarah', 'Christopher', 'Jessica', 'Daniel', 'Emily', 'Matthew'],
  lastNames: ['Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Miller', 'Davis', 'Wilson', 'Anderson', 'Taylor'],
  cities: ['Portland, Oregon', 'Boise, Idaho', 'Raleigh, North Carolina', 'Tampa, Florida', 'Omaha, Nebraska', 'Tucson, Arizona', 'Nashville, Tennessee', 'Milwaukee, Wisconsin', 'Sacramento, California', 'Richmond, Virginia', 'Savannah, Georgia', 'Boulder, Colorado', 'Spokane, Washington', 'Lexington, Kentucky', 'Des Moines, Iowa', 'Knoxville, Tennessee', 'Chattanooga, Tennessee', 'Baton Rouge, Louisiana', 'Asheville, North Carolina', 'Santa Fe, New Mexico']
}, {
  code: 'GB',
  firstNames: ['Oliver', 'Olivia', 'George', 'Amelia', 'Harry', 'Isla', 'Jack', 'Emily', 'Charlie', 'Mia', 'Thomas', 'Sophie', 'James', 'Grace', 'William'],
  lastNames: ['Smith', 'Jones', 'Williams', 'Taylor', 'Brown', 'Davies', 'Evans', 'Wilson', 'Thomas', 'Roberts'],
  cities: ['Bath, Somerset', 'York, North Yorkshire', 'Canterbury, Kent', 'Norwich, Norfolk', 'Exeter, Devon', 'Chester, Cheshire', 'Brighton, East Sussex', 'Oxford, Oxfordshire', 'Cambridge, Cambridgeshire', 'Stratford-upon-Avon, Warwickshire', 'Durham, County Durham', 'Salisbury, Wiltshire', 'Winchester, Hampshire', 'Harrogate, North Yorkshire', 'St Albans, Hertfordshire']
}, {
  code: 'CA',
  firstNames: ['Liam', 'Noah', 'William', 'James', 'Benjamin', 'Lucas', 'Emma', 'Olivia', 'Charlotte', 'Amelia', 'Ethan', 'Mason', 'Jacob', 'Logan', 'Alexander'],
  lastNames: ['Smith', 'Brown', 'Tremblay', 'Martin', 'Roy', 'Wilson', 'MacDonald', 'Taylor', 'Campbell', 'Anderson'],
  cities: ['Kelowna, British Columbia', 'Thunder Bay, Ontario', 'Red Deer, Alberta', 'Lethbridge, Alberta', 'Nanaimo, British Columbia', 'Kamloops, British Columbia', 'Chilliwack, British Columbia', 'Victoria, British Columbia', 'Halifax, Nova Scotia', "St. John's, Newfoundland", 'Moncton, New Brunswick', 'Fredericton, New Brunswick', 'Charlottetown, Prince Edward Island', 'Guelph, Ontario', 'Kingston, Ontario']
}, {
  code: 'AU',
  firstNames: ['Oliver', 'Noah', 'Jack', 'William', 'Leo', 'Henry', 'Charlotte', 'Amelia', 'Isla', 'Olivia', 'James', 'Thomas', 'Lachlan', 'Ethan', 'Cooper'],
  lastNames: ['Smith', 'Jones', 'Williams', 'Brown', 'Wilson', 'Taylor', 'Johnson', 'White', 'Martin', 'Anderson'],
  cities: ['Ballarat, Victoria', 'Toowoomba, Queensland', 'Bendigo, Victoria', 'Albury, New South Wales', 'Launceston, Tasmania', 'Mackay, Queensland', 'Rockhampton, Queensland', 'Bunbury, Western Australia', 'Bundaberg, Queensland', 'Wagga Wagga, New South Wales', 'Hervey Bay, Queensland', 'Mildura, Victoria', 'Warrnambool, Victoria', 'Gladstone, Queensland', 'Tamworth, New South Wales']
}, {
  code: 'NZ',
  firstNames: ['Oliver', 'Jack', 'Leo', 'Charlie', 'Liam', 'Noah', 'Charlotte', 'Amelia', 'Isla', 'Olivia', 'James', 'William', 'Thomas', 'George', 'Henry'],
  lastNames: ['Smith', 'Williams', 'Brown', 'Wilson', 'Taylor', 'Anderson', 'Thomas', 'Harris', 'Jones', 'Martin'],
  cities: ['Nelson, Nelson', "Napier, Hawke's Bay", "Hastings, Hawke's Bay", 'Tauranga, Bay of Plenty', 'Palmerston North, Manawatū-Whanganui', 'Rotorua, Bay of Plenty', 'New Plymouth, Taranaki', 'Whangārei, Northland', 'Invercargill, Southland', 'Whanganui, Manawatū-Whanganui', 'Gisborne, Gisborne', 'Blenheim, Marlborough', 'Timaru, Canterbury', 'Taupō, Waikato', 'Masterton, Wellington']
}, {
  code: 'IE',
  firstNames: ['Jack', 'James', 'Noah', 'Conor', 'Daniel', 'Liam', 'Emily', 'Grace', 'Fiadh', 'Sophie', 'Sean', 'Patrick', 'Cian', 'Oisín', 'Finn'],
  lastNames: ['Murphy', 'Kelly', "O'Sullivan", 'Walsh', 'Smith', "O'Brien", 'Byrne', 'Ryan', "O'Connor", "O'Neill"],
  cities: ['Galway, County Galway', 'Limerick, County Limerick', 'Waterford, County Waterford', 'Drogheda, County Louth', 'Dundalk, County Louth', 'Bray, County Wicklow', 'Navan, County Meath', 'Kilkenny, County Kilkenny', 'Ennis, County Clare', 'Carlow, County Carlow', 'Tralee, County Kerry', 'Newbridge, County Kildare', 'Portlaoise, County Laois', 'Balbriggan, County Dublin', 'Naas, County Kildare']
}, {
  code: 'DE',
  firstNames: ['Lukas', 'Leon', 'Finn', 'Paul', 'Jonas', 'Ben', 'Emma', 'Mia', 'Hannah', 'Sophia', 'Maximilian', 'Felix', 'Alexander', 'Elias', 'Noah'],
  lastNames: ['Müller', 'Schmidt', 'Schneider', 'Fischer', 'Weber', 'Meyer', 'Wagner', 'Becker', 'Schulz', 'Hoffmann'],
  cities: ['Freiburg, Germany', 'Lübeck, Germany', 'Erfurt, Germany', 'Mainz, Germany', 'Kassel, Germany', 'Hagen, Germany', 'Saarbrücken, Germany', 'Potsdam, Germany', 'Ludwigshafen, Germany', 'Oldenburg, Germany', 'Osnabrück, Germany', 'Leverkusen, Germany', 'Heidelberg, Germany', 'Darmstadt, Germany', 'Regensburg, Germany']
}, {
  code: 'FR',
  firstNames: ['Gabriel', 'Raphaël', 'Léo', 'Louis', 'Lucas', 'Adam', 'Emma', 'Jade', 'Louise', 'Alice', 'Hugo', 'Jules', 'Arthur', 'Nathan', 'Thomas'],
  lastNames: ['Martin', 'Bernard', 'Dubois', 'Thomas', 'Robert', 'Richard', 'Petit', 'Durand', 'Leroy', 'Moreau'],
  cities: ['Dijon, France', 'Angers, France', 'Nîmes, France', 'Villeurbanne, France', 'Le Mans, France', 'Aix-en-Provence, France', 'Clermont-Ferrand, France', 'Brest, France', 'Tours, France', 'Amiens, France', 'Limoges, France', 'Annecy, France', 'Perpignan, France', 'Boulogne-Billancourt, France', 'Metz, France']
}, {
  code: 'ES',
  firstNames: ['Hugo', 'Mateo', 'Martín', 'Lucas', 'Leo', 'Daniel', 'Lucía', 'Sofía', 'Martina', 'María', 'Pablo', 'Alejandro', 'Adrián', 'Carlos', 'Diego'],
  lastNames: ['García', 'Rodríguez', 'Martínez', 'López', 'González', 'Hernández', 'Pérez', 'Sánchez', 'Ramírez', 'Torres'],
  cities: ['Murcia, Spain', 'Palma, Spain', 'Las Palmas, Spain', 'Alicante, Spain', 'Córdoba, Spain', 'Valladolid, Spain', 'Vigo, Spain', 'Gijón, Spain', "L'Hospitalet de Llobregat, Spain", 'Vitoria-Gasteiz, Spain', 'A Coruña, Spain', 'Granada, Spain', 'Elche, Spain', 'Oviedo, Spain', 'Badalona, Spain']
}, {
  code: 'IT',
  firstNames: ['Leonardo', 'Francesco', 'Alessandro', 'Lorenzo', 'Mattia', 'Andrea', 'Sofia', 'Giulia', 'Aurora', 'Alice', 'Marco', 'Luca', 'Giuseppe', 'Giovanni', 'Antonio'],
  lastNames: ['Rossi', 'Russo', 'Ferrari', 'Esposito', 'Bianchi', 'Romano', 'Colombo', 'Ricci', 'Marino', 'Greco'],
  cities: ['Bari, Italy', 'Catania, Italy', 'Venice, Italy', 'Verona, Italy', 'Messina, Italy', 'Padua, Italy', 'Trieste, Italy', 'Taranto, Italy', 'Brescia, Italy', 'Parma, Italy', 'Prato, Italy', 'Modena, Italy', 'Reggio Calabria, Italy', 'Reggio Emilia, Italy', 'Perugia, Italy']
}, {
  code: 'NL',
  firstNames: ['Noah', 'Sem', 'Liam', 'Lucas', 'Daan', 'Finn', 'Emma', 'Julia', 'Mila', 'Sophie', 'Bram', 'Jesse', 'Milan', 'Luuk', 'Thomas'],
  lastNames: ['De Jong', 'Jansen', 'De Vries', 'Van den Berg', 'Van Dijk', 'Bakker', 'Janssen', 'Visser', 'Smit', 'Meijer'],
  cities: ['Tilburg, Netherlands', 'Almere, Netherlands', 'Groningen, Netherlands', 'Breda, Netherlands', 'Nijmegen, Netherlands', 'Enschede, Netherlands', 'Apeldoorn, Netherlands', 'Haarlem, Netherlands', 'Amersfoort, Netherlands', 'Zaanstad, Netherlands', 'Arnhem, Netherlands', 'Haarlemmermeer, Netherlands', "'s-Hertogenbosch, Netherlands", 'Zoetermeer, Netherlands', 'Zwolle, Netherlands']
}, {
  code: 'SE',
  firstNames: ['Lucas', 'Liam', 'William', 'Elias', 'Noah', 'Hugo', 'Alice', 'Maja', 'Elsa', 'Astrid', 'Oscar', 'Alexander', 'Viktor', 'Erik', 'Emil'],
  lastNames: ['Andersson', 'Johansson', 'Karlsson', 'Nilsson', 'Eriksson', 'Larsson', 'Olsson', 'Persson', 'Svensson', 'Gustafsson'],
  cities: ['Västerås, Sweden', 'Örebro, Sweden', 'Linköping, Sweden', 'Helsingborg, Sweden', 'Jönköping, Sweden', 'Norrköping, Sweden', 'Lund, Sweden', 'Umeå, Sweden', 'Gävle, Sweden', 'Borås, Sweden', 'Södertälje, Sweden', 'Eskilstuna, Sweden', 'Halmstad, Sweden', 'Växjö, Sweden', 'Karlstad, Sweden']
}, {
  code: 'NO',
  firstNames: ['Jakob', 'Emil', 'Noah', 'Oliver', 'Filip', 'William', 'Emma', 'Nora', 'Ella', 'Maja', 'Magnus', 'Henrik', 'Lars', 'Erik', 'Kristian'],
  lastNames: ['Hansen', 'Johansen', 'Olsen', 'Larsen', 'Andersen', 'Pedersen', 'Nilsen', 'Kristiansen', 'Jensen', 'Karlsen'],
  cities: ['Bærum, Norway', 'Kristiansand, Norway', 'Fredrikstad, Norway', 'Sandnes, Norway', 'Tromsø, Norway', 'Drammen, Norway', 'Asker, Norway', 'Sarpsborg, Norway', 'Skien, Norway', 'Bodø, Norway', 'Sandefjord, Norway', 'Ålesund, Norway', 'Larvik, Norway', 'Arendal, Norway', 'Tønsberg, Norway']
}, {
  code: 'DK',
  firstNames: ['William', 'Oscar', 'Lucas', 'Oliver', 'Noah', 'Victor', 'Emma', 'Alma', 'Ida', 'Freja', 'Magnus', 'Frederik', 'Mikkel', 'Christian', 'Mads'],
  lastNames: ['Nielsen', 'Jensen', 'Hansen', 'Pedersen', 'Andersen', 'Christensen', 'Larsen', 'Sørensen', 'Rasmussen', 'Jørgensen'],
  cities: ['Esbjerg, Denmark', 'Randers, Denmark', 'Kolding, Denmark', 'Horsens, Denmark', 'Vejle, Denmark', 'Roskilde, Denmark', 'Herning, Denmark', 'Hørsholm, Denmark', 'Helsingør, Denmark', 'Silkeborg, Denmark', 'Næstved, Denmark', 'Fredericia, Denmark', 'Viborg, Denmark', 'Køge, Denmark', 'Holstebro, Denmark']
}];
const products = [{
  name: 'Complete Digital Edition',
  price: 'USD $29.97',
  image: 'https://cdn.magicpatterns.com/uploads/drsCGCcawJQmxdkJUsQyKs/Holiday_Edition_Digital_Set.png'
}, {
  name: 'Complete Digital Collection',
  price: 'USD $59.97',
  image: 'https://cdn.magicpatterns.com/uploads/drsCGCcawJQmxdkJUsQyKs/Holiday_Edition_Digital_Set.png'
}];
const SocialProofNotificationInt02 = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentNotification, setCurrentNotification] = useState<any>(null);
  const lastCountryCodeRef = useRef<string | null>(null);
  const generateRandomNotification = () => {
    // Filter out the last used country to ensure consecutive notifications are from different countries
    const availableCountries = TIER1_COUNTRIES.filter(c => c.code !== lastCountryCodeRef.current);
    const country = availableCountries[Math.floor(Math.random() * availableCountries.length)];
    lastCountryCodeRef.current = country.code;
    const firstName = country.firstNames[Math.floor(Math.random() * country.firstNames.length)];
    const lastName = country.lastNames[Math.floor(Math.random() * country.lastNames.length)];
    const city = country.cities[Math.floor(Math.random() * country.cities.length)];
    const product = products[Math.floor(Math.random() * products.length)];
    return {
      name: `${firstName} ${lastName}`,
      city: city,
      product: product.name,
      price: product.price,
      image: product.image
    };
  };
  useEffect(() => {
    const getRandomInterval = () => Math.floor(Math.random() * 2000) + 12000; // 12-14 seconds
    const showNotification = () => {
      setCurrentNotification(generateRandomNotification());
      setIsVisible(true);
      setTimeout(() => {
        setIsVisible(false);
      }, 4000);
    };
    const initialDelay = setTimeout(() => {
      showNotification();
    }, 12000);
    let timeoutId: ReturnType<typeof setTimeout>;
    const scheduleNext = () => {
      timeoutId = setTimeout(() => {
        showNotification();
        scheduleNext();
      }, getRandomInterval());
    };
    scheduleNext();
    return () => {
      clearTimeout(initialDelay);
      clearTimeout(timeoutId);
    };
  }, []);
  if (!currentNotification) return null;
  return <div className={`fixed z-30 transition-all duration-500 ease-out
        bottom-4 left-1/2 -translate-x-1/2
        md:bottom-5 md:left-5 md:translate-x-0
        ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0 pointer-events-none'}`} data-id="element-846">
      <div className="relative backdrop-blur-md rounded-lg border shadow-[0_4px_20px_rgba(0,0,0,0.5)] p-3 flex items-center gap-3 w-[260px] sm:w-[280px]" style={{
      backgroundColor: 'rgba(11,11,11,0.95)',
      borderColor: 'rgba(255,108,0,0.3)'
    }} data-id="element-847">
        <button onClick={() => setIsVisible(false)} className="absolute top-1.5 right-1.5 transition-colors z-10" style={{
        color: 'rgba(245,245,245,0.6)'
      }} aria-label="Close notification" data-id="element-848">
          <XIcon className="h-3 w-3" data-id="element-849" />
        </button>

        <div className="flex-shrink-0" data-id="element-850">
          <div className="w-12 h-12 rounded-md overflow-hidden border border-orange-500/20" data-id="element-851">
            <img src={currentNotification.image} alt="Book" className="w-full h-full object-cover" data-id="element-852" />
          </div>
        </div>

        <div className="flex-1 min-w-0 pr-4" data-id="element-853">
          <div className="flex items-center gap-1 mb-0.5" data-id="element-854">
            <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" data-id="element-855"></div>
            <span className="text-[10px] font-semibold text-green-400 uppercase tracking-wide" data-id="element-856">
              Just Purchased
            </span>
          </div>
          <p className="text-xs font-semibold text-white truncate" style={{
          fontFamily: 'Inter, sans-serif'
        }} data-id="element-857">
            {currentNotification.name}
          </p>
          <p className="text-[10px] truncate" style={{
          color: 'rgba(245,245,245,0.6)',
          fontFamily: 'Inter, sans-serif'
        }} data-id="element-858">
            {currentNotification.city}
          </p>
          <p className="text-[10px] font-semibold mt-0.5" style={{
          color: ACCENT,
          fontFamily: 'Inter, sans-serif'
        }} data-id="element-859">
            {currentNotification.product}
          </p>
        </div>
      </div>
    </div>;
};
// ─── LANDING INT02 EXPORT ────────────────────────────────────────────────────────
export function LandingInt02() {
  return <div className="w-full min-h-screen" style={{
    backgroundColor: BG,
    color: '#F5F5F5'
  }} data-id="element-860">
      <main data-id="element-861">
        <HeroInt02 data-id="element-862" />
        <IdentificationInt02 data-id="element-863" />
        <WhatYouGetInt02 data-id="element-864" />
        <TestimonialsInt02 data-id="element-865" />
        <PurchaseInt02 data-id="element-866" />
        <FAQInt02 data-id="element-867" />
      </main>
      <Footer data-id="element-868" />
      <SocialProofNotificationInt02 data-id="element-869" />
    </div>;
}